/* ───────────────────────────────────────────────────────────────
 *  IMPORTS
 * ──────────────────────────────────────────────────────────── */
import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';

import {
  List,
  SimpleForm,
  TextInput,
  TextField,
  DateField,
  Resource,
  Filter,
  Show,
  Datagrid,
  useRecordContext,
  useNotify,
  useListContext,
} from 'react-admin';

import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';

import {
  Tooltip,
  Typography,
  makeStyles,
  Paper,
  Button,
  Snackbar,
  CircularProgress,
} from '@material-ui/core';

import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import CodeIcon from '@material-ui/icons/Code';
import FormatAlignLeftIcon from '@material-ui/icons/FormatAlignLeft';
import GetAppIcon from '@material-ui/icons/GetApp';

/* Visor JSON */
import { JsonField } from 'react-admin-json-view';

import { useUpdateDom } from '../hooks/useUpdateDom';
import { hostname } from '../providers/api';
import { useTokenOps } from '../components/TokenContext';
import { useCalculationLogProgress } from './calculationLogProgress';
import CalculationProgress from './project/components/CalculationProgress';
import {
  CalculationWarningsTable,
  highestSeverity,
  parseGroupedWarnings,
  SEVERITIES,
  WarningCountChips,
  WarningsField,
} from './calculationLogWarnings';


/* ───────────────────────────────────────────────────────────────
 *  COMPONENTES AUXILIARES
 * ──────────────────────────────────────────────────────────── */
const RunProgressContext = createContext({});
const CalculationDuration = ({ record: providedRecord }) => {
  const record = useRecordContext({ record: providedRecord }) || {};
  const milliseconds = record.timeTakenMs;
  if (typeof milliseconds !== 'number' || !Number.isFinite(milliseconds) || milliseconds < 0) {
    return <span title="Duration was not recorded">—</span>;
  }
  const seconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const duration = milliseconds < 1000
    ? `${milliseconds}ms`
    : [hours && `${hours}h`, (hours || minutes) && `${minutes}m`, `${seconds % 60}s`]
      .filter(Boolean).join(' ');
  return <span title="Recorded elapsed time; includes queue time for isolated runs">{duration}</span>;
};

export const LogProgressProvider = ({ children }) => {
  const { data = {}, ids = [] } = useListContext();
  const tokenOps = useTokenOps();
  const records = ids.map((id) => data[id]).filter(Boolean);
  const progress = useCalculationLogProgress(
    records,
    tokenOps.account?.data?.accountId
  );
  return (
    <RunProgressContext.Provider value={progress}>
      {children}
    </RunProgressContext.Provider>
  );
};
export const LogProgressStatus = ({ record: providedRecord }) => {
  const record = useRecordContext({ record: providedRecord }) || {};
  const progress = useContext(RunProgressContext)[record.id];
  // Polling can finish a run before the list row is refreshed.
  const runStatuses = {
    preparing: 'QUEUED', requested: 'QUEUED', running: 'CALCULATING',
    ready: 'CALCULATING', succeeded: 'SUCCESS', failed: 'FAILED',
  };
  const status = ['SUCCESS', 'FAILED'].includes(record.status)
    ? record.status : runStatuses[progress?.runStatus] || record.status;
  return status === 'CALCULATING' && progress ? (
    <CalculationProgress status={progress} />
  ) : (
    <StatusIcon record={{ ...record, status }} />
  );
};

const StatusIcon = ({ record = {} }) => {
  const { status } = record;
  let icon = null;

  switch (status) {
    case 'PROCESSING':
      icon = <HourglassEmptyIcon htmlColor="orange" />;
      break;
    case 'SUCCESS':
      icon = <CheckCircleOutlineIcon htmlColor="green" />;
      break;
    case 'FAILED':
      icon = <ErrorOutlineIcon htmlColor="red" />;
      break;
    default:
      icon = null;
  }

  // Si no hay icono, retornar el status como texto sin Tooltip
  if (!icon) {
    return <span>{status || '-'}</span>;
  }

  return <Tooltip title={status || ''}><span>{icon}</span></Tooltip>;
};

const Filters = (props) => (
  <Filter {...props}>
    <TextInput label="Search" source="name" alwaysOn />
  </Filter>
);

/* ───────────────────────────────────────────────────────────────
 *  ESTILOS
 * ──────────────────────────────────────────────────────────── */
const useStyles = makeStyles((theme) => ({
  root: { marginTop: theme.spacing(1), marginBottom: theme.spacing(1) },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.grey[100],
    cursor: 'pointer',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    '& > *': { marginLeft: theme.spacing(1) },
  },
  content: {
    padding: theme.spacing(2),
    maxHeight: 500,
    overflow: 'auto',
    backgroundColor: theme.palette.grey[50],
  },
  plainText: {
    whiteSpace: 'pre-wrap',
    padding: theme.spacing(2),
    maxHeight: 500,
    overflow: 'auto',
    fontFamily: 'monospace',
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
  },
}));

/* ───────────────────────────────────────────────────────────────
 *  SUCCESS FORMATTER
 * ──────────────────────────────────────────────────────────── */
const SuccessFormatter = () => {
  const classes = useStyles();
  const record = useRecordContext();

  const [expanded, setExpanded] = useState(true);
  const [viewMode, setViewMode] = useState('formatted'); // 'formatted' | 'raw'
  const [copyOk, setCopyOk] = useState(false);
  const [data, setData] = useState(null); // obj | string
  const [raw, setRaw] = useState('');

  /* PRE-PROCESA string para convertirlo en JSON válido */
  const preProcess = (str) => {
    // 1. quitar "Success:" al inicio si existe
    let cleaned = str.replace(/^Success:\s*/i, '').trim();

    // 2. si no empieza con "[" lo convertimos en array
    if (!cleaned.startsWith('[')) {
      // 3. unir objetos contiguos "} {", "}\n{" → "},{"
      cleaned = cleaned.replace(/}\s*,?\s*{/g, '},{');
      cleaned = `[${cleaned}]`;
    }
    return cleaned;
  };

  /* parseo inicial */
  useEffect(() => {
    if (!record?.warnings) return;

    const rawStr =
      typeof record.warnings === 'string'
        ? record.warnings
        : JSON.stringify(record.warnings, null, 2);

    setRaw(rawStr);

    if (typeof record.warnings === 'string') {
      let parsed = null;
      try {
        parsed = JSON.parse(record.warnings); // ¿ya es JSON?
      } catch {
        try {
          parsed = JSON.parse(preProcess(record.warnings)); // plan B
        } catch {
          /* sigue fallando → se mostrará como texto */
        }
      }
      setData(parsed || record.warnings);
      if (!parsed) setViewMode('raw');
    } else {
      setData(record.warnings);
    }
  }, [record]);

  /* copiar al portapapeles */
  const handleCopy = useCallback(() => {
    if (!data) return;
    const txt = typeof data === 'object' ? JSON.stringify(data, null, 2) : String(data);
    navigator.clipboard.writeText(txt).then(() => {
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 2000);
    });
  }, [data]);

  // Solo mostrar cuando hay warnings y el estado es SUCCESS
  if (!record?.warnings || record?.warnings === "" || record?.status !== 'SUCCESS') return null;

  // LCA warnings grouped by root cause (IT-3326): the header reflects the worst severity.
  const groups = parseGroupedWarnings(data);
  const worst = groups && highestSeverity(groups);
  const HeaderIcon = { error: ErrorIcon, warning: WarningIcon }[worst] || CheckCircleIcon;
  const headerColor = worst === 'error' || worst === 'warning' ? SEVERITIES[worst].color : 'green';

  return (
    <Paper className={classes.root} variant="outlined">
      {/* CABECERA */}
      <div className={classes.header}>
        <div onClick={() => setExpanded(!expanded)}>
          <Typography variant="subtitle2" style={{ display: 'flex', alignItems: 'center' }}>
            <HeaderIcon fontSize="small" style={{ marginRight: 8, color: headerColor }} />
            {expanded ? 'Detalles del cálculo' : 'Expandir detalles del cálculo'}
            {groups?.length > 0 && (
              <span style={{ marginLeft: 12 }}>
                <WarningCountChips warnings={groups} />
              </span>
            )}
          </Typography>
        </div>

        <div className={classes.headerActions}>
          {expanded && (
            <>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileCopyIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy();
                }}
              >
                Copiar
              </Button>

              <Button
                size="small"
                variant="outlined"
                startIcon={viewMode === 'formatted' ? <CodeIcon /> : <FormatAlignLeftIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setViewMode(viewMode === 'formatted' ? 'raw' : 'formatted');
                }}
              >
                {viewMode === 'formatted' ? 'Ver como texto' : 'Ver formateado'}
              </Button>
            </>
          )}
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </div>
      </div>

      {/* CONTENIDO */}
      {expanded &&
        (viewMode === 'formatted' ? (
          <div className={classes.content}>
            {groups?.length > 0 ? (
              <CalculationWarningsTable groups={groups} />
            ) : typeof data === 'object' ? (
              <JsonField
                record={{ warnings: data }} // wrapper ficticio
                source="warnings"
                jsonString={false}
                reactJsonOptions={{
                  name: null,
                  collapsed: 1,
                  displayDataTypes: false,
                  enableClipboard: false,
                  collapseStringsAfterLength: 100,
                }}
              />
            ) : (
              <pre className={classes.plainText}>{data}</pre>
            )}
          </div>
        ) : (
          <pre className={classes.plainText}>{raw}</pre>
        ))}

      <Snackbar
        open={copyOk}
        message="Copiado al portapapeles"
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Paper>
  );
};

/* ───────────────────────────────────────────────────────────────
 *  ERROR FORMATTER
 * ──────────────────────────────────────────────────────────── */
const ErrorFormatter = () => {
  const classes = useStyles();
  const record = useRecordContext();

  const [expanded, setExpanded] = useState(true);
  const [viewMode, setViewMode] = useState('formatted'); // 'formatted' | 'raw'
  const [copyOk, setCopyOk] = useState(false);
  const [data, setData] = useState(null); // obj | string
  const [raw, setRaw] = useState('');

  /* PRE-PROCESA string para convertirlo en JSON válido */
  const preProcess = (str) => {
    // 1. quitar "Error:" al inicio
    let cleaned = str.replace(/^Error:\s*/i, '').trim();

    // 2. si no empieza con "[" lo convertimos en array
    if (!cleaned.startsWith('[')) {
      // 3. unir objetos contiguos "} {", "}\n{" → "},{"
      cleaned = cleaned.replace(/}\s*,?\s*{/g, '},{');
      cleaned = `[${cleaned}]`;
    }
    return cleaned;
  };

  /* parseo inicial */
  useEffect(() => {
    if (!record?.stacktrace) return;

    const rawStr =
      typeof record.stacktrace === 'string'
        ? record.stacktrace
        : JSON.stringify(record.stacktrace, null, 2);

    setRaw(rawStr);

    if (typeof record.stacktrace === 'string') {
      let parsed = null;
      try {
        parsed = JSON.parse(record.stacktrace); // ¿ya es JSON?
      } catch {
        try {
          parsed = JSON.parse(preProcess(record.stacktrace)); // plan B
        } catch {
          /* sigue fallando → se mostrará como texto */
        }
      }
      setData(parsed || record.stacktrace);
      if (!parsed) setViewMode('raw');
    } else {
      setData(record.stacktrace);
    }
  }, [record]);

  /* copiar al portapapeles */
  const handleCopy = useCallback(() => {
    if (!data) return;
    const txt = typeof data === 'object' ? JSON.stringify(data, null, 2) : String(data);
    navigator.clipboard.writeText(txt).then(() => {
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 2000);
    });
  }, [data]);

  // Si no hay datos de error o el estado no es FAILED, no mostramos nada
  if (!record?.stacktrace || record?.status !== 'FAILED') return null;

  return (
    <Paper className={classes.root} variant="outlined">
      {/* CABECERA */}
      <div className={classes.header}>
        <div onClick={() => setExpanded(!expanded)}>
          <Typography variant="subtitle2" style={{ display: 'flex', alignItems: 'center' }}>
            <ErrorOutlineIcon fontSize="small" style={{ marginRight: 8 }} />
            {expanded ? 'Detalles de error' : 'Expandir detalles de error'}
          </Typography>
        </div>

        <div className={classes.headerActions}>
          {expanded && (
            <>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileCopyIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy();
                }}
              >
                Copiar
              </Button>

              <Button
                size="small"
                variant="outlined"
                startIcon={viewMode === 'formatted' ? <CodeIcon /> : <FormatAlignLeftIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setViewMode(viewMode === 'formatted' ? 'raw' : 'formatted');
                }}
              >
                {viewMode === 'formatted' ? 'Ver como texto' : 'Ver formateado'}
              </Button>
            </>
          )}
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </div>
      </div>

      {/* CONTENIDO */}
      {expanded &&
        (viewMode === 'formatted' ? (
          <div className={classes.content}>
            {typeof data === 'object' ? (
              <JsonField
                record={{ stacktrace: data }} // wrapper ficticio
                source="stacktrace"
                jsonString={false}
                reactJsonOptions={{
                  name: null,
                  collapsed: 1,
                  displayDataTypes: false,
                  enableClipboard: false,
                  collapseStringsAfterLength: 100,
                }}
              />
            ) : (
              <pre className={classes.plainText}>{data}</pre>
            )}
          </div>
        ) : (
          <pre className={classes.plainText}>{raw}</pre>
        ))}

      <Snackbar
        open={copyOk}
        message="Copiado al portapapeles"
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Paper>
  );
};

/* ───────────────────────────────────────────────────────────────
 *  LISTA Y SHOW
 * ──────────────────────────────────────────────────────────── */
const CalculationLogsList = (props) => {
  useUpdateDom();
  return (
    <List {...props} filters={Filters(props)} sort={{ field: 'lastStatusDate', order: 'DESC' }}>
      <LogProgressProvider>
      <div style={{ marginTop: 15 }}>
        <Datagrid rowClick="show">
          <TextField source="type" label="Type" />
          <TextField source="accountName" label="Account" />
          <TextField source="projectname" label="Project name" />
          <LogProgressStatus label="Status" source="status" />
          <CalculationDuration source="timeTakenMs" label="Duration" />
          <WarningsField label="Warnings" sortable={false} />
          <DateField showTime source="lastStatusDate" label="Execution Date" />
          <TextField source="runVersion" label="Version" />
        </Datagrid>
      </div>
      </LogProgressProvider>
    </List>
  );
};

const DownloadLogsButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const tokenOps = useTokenOps();
  const [downloading, setDownloading] = useState(false);


  // Solo mostrar el botón para logs de Ecobalyse (cubre variantes como _TEST)
  if (!record?.type || !record.type.startsWith('ECOBALYSE_')) {
    return null;
  }

  const handleDownload = async () => {
    if (!record?.id) {
      notify('No se pudo obtener el ID del registro', { type: 'error' });
      return;
    }

    setDownloading(true);
    try {
      const url = `${hostname}/calculationLog/download/${record.id}`;
      const accessToken = tokenOps.account?.accessToken || tokenOps.root?.accessToken;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': accessToken ? `Bearer ${accessToken}` : '',
        },
      });

      if (!response.ok) {
        if (response.status === 404) throw new Error('Archivo no encontrado');
        throw new Error('Error al descargar el archivo');
      }

      const blob = await response.blob();
      const XLSX = await import('xlsx');

      const finalFilename = `calculation-log-${record.id}.xlsx`;
      let finalBlob;

      try {
        // Intentar leer como Excel (XLSX es un ZIP internamente)
        const arrayBuffer = await blob.arrayBuffer();
        const srcWorkbook = XLSX.read(new Uint8Array(arrayBuffer), { type: 'array' });

        const srcSheet = srcWorkbook.Sheets[srcWorkbook.SheetNames[0]];
        if (!srcSheet) throw new Error('No se encontró hoja de cálculo');

        // Convertir a JSON para inspeccionar los headers
        const jsonData = XLSX.utils.sheet_to_json(srcSheet, { defval: '' });
        if (jsonData.length === 0) throw new Error('Hoja de cálculo vacía');

        // Buscar columnas que correspondan a Referencia, GTINs, Errores
        const headers = Object.keys(jsonData[0]);
        const findKey = (keywords) =>
          headers.find(h => keywords.some(k => h.toLowerCase().includes(k)));

        const refKey = findKey(['referencia', 'reference', 'ref']);
        const gtinKey = findKey(['gtin']);
        const errorKey = findKey(['error', 'errores']);

        // Si no se encontraron headers, usar las primeras 3 columnas posicionales
        const fallbackKeys = !refKey && !gtinKey && !errorKey;
        const posKeys = fallbackKeys ? headers.slice(0, 3) : [];

        // Mapear los datos a las 3 columnas requeridas
        const mappedData = jsonData.map(row => ({
          'Referencia': refKey ? String(row[refKey] ?? '') :
                        (fallbackKeys && posKeys[0] ? String(row[posKeys[0]] ?? '') : ''),
          'GTINs':      gtinKey ? String(row[gtinKey] ?? '') :
                        (fallbackKeys && posKeys[1] ? String(row[posKeys[1]] ?? '') : ''),
          'Errores':    errorKey ? String(row[errorKey] ?? '') :
                        (fallbackKeys && posKeys[2] ? String(row[posKeys[2]] ?? '') : ''),
        }));

        // Crear nuevo workbook con las 3 columnas
        const newWorkbook = XLSX.utils.book_new();
        const newSheet = XLSX.utils.json_to_sheet(mappedData);
        newSheet['!cols'] = [{ wch: 30 }, { wch: 25 }, { wch: 50 }];
        XLSX.utils.book_append_sheet(newWorkbook, newSheet, 'Ecobalyse Logs');

        const xlsxBuffer = XLSX.write(newWorkbook, { bookType: 'xlsx', type: 'array' });
        finalBlob = new Blob([xlsxBuffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

      } catch (excelReadError) {
        // Fallback: el contenido no es Excel, intentar como texto/JSON
        console.error('No se pudo leer como Excel, intentando como texto:', excelReadError);

        const text = await blob.text();
        let rows = [];

        try {
          let parsed = JSON.parse(text);
          if (!Array.isArray(parsed)) parsed = [parsed];

          rows = parsed.map(item => ({
            'Referencia': String(item.reference || item.referencia || item.ref || ''),
            'GTINs':      String(item.gtin || item.gtins || item.GTIN || ''),
            'Errores':    String(item.error || item.errores || item.message || ''),
          }));
        } catch {
          rows = [{ 'Referencia': '', 'GTINs': '', 'Errores': text.substring(0, 32000) }];
        }

        const newWorkbook = XLSX.utils.book_new();
        const newSheet = XLSX.utils.json_to_sheet(rows);
        newSheet['!cols'] = [{ wch: 30 }, { wch: 25 }, { wch: 50 }];
        XLSX.utils.book_append_sheet(newWorkbook, newSheet, 'Ecobalyse Logs');

        const xlsxBuffer = XLSX.write(newWorkbook, { bookType: 'xlsx', type: 'array' });
        finalBlob = new Blob([xlsxBuffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
      }

      // Descargar el archivo
      const downloadUrl = window.URL.createObjectURL(finalBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = finalFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      notify('Archivo descargado correctamente', { type: 'success' });
    } catch (error) {
      console.error('Error al descargar:', error);
      notify(error.message || 'Error al descargar el archivo', { type: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Button
      variant="contained"
      color="primary"
      startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <GetAppIcon />}
      onClick={handleDownload}
      disabled={downloading}
      style={{ marginBottom: 16 }}
    >
      {downloading ? 'Descargando...' : 'Download Logs'}
    </Button>
  );
};

const ShowCalculationLogs = (props) => {
  useUpdateDom();
  return (
    <Show {...props}>
      <SimpleForm toolbar={null}>
        <DownloadLogsButton />
        <TextField source="type" label="Type" />
        <TextField source="accountName" label="Account" />
        <TextField source="projectname" label="Project name" />
        <TextField source="status" />
        <StatusIcon label="Status" />
        <CalculationDuration source="timeTakenMs" label="Duration" />
        <DateField showTime source="lastStatusDate" label="Execution Date" />
        <TextField source="runVersion" label="Version" />
        {/* Error details formatter */}
        <ErrorFormatter />
        {/* Success details formatter */}
        <SuccessFormatter />
      </SimpleForm>
    </Show>
  );
};

/* ───────────────────────────────────────────────────────────────
 *  RESOURCE
 * ──────────────────────────────────────────────────────────── */
const calculationLogsResource = () => (
  <Resource
    name="calculationLog"
    options={{ label: 'Calculation logs', menuGroup: 'Upload logs' }}
    list={CalculationLogsList}
    show={ShowCalculationLogs}
  />
);

export default calculationLogsResource;
